/**
 * Teams — two or more agents in the brokerage who work together (Dawn, 2026-10-28: "create teams on
 * the agent page by selecting multiple agents", "teams need their own section on the agent roster
 * page", "BLT needs to be able to create teams").
 *
 * One source of truth. A team is stored in the site config's `content.teams[]`, beside the agent
 * cards (`content.agent_profiles[]`) and the offices (`content.offices[]`), and written through the
 * same PUT /site-config content write (the Agents tab) or the same config seam (BLT, gateway
 * lib/site-config-write.ts mutateSiteConfig). It is drafted and goes live on Publish.
 *
 * A team holds the agents' roster keys, never a copy of an agent: names, photos and phones always
 * come from the roster (GET /mls/agents, the MLS feed with the agent cards laid over it). A member
 * the roster no longer has, or one the owner hid, simply drops out of the team.
 *
 * ONE definition, read by the live pages (apps/tenant-sites), the Studio and the Agents tab
 * (apps/builder) and BLT (the gateway mirror lib/teams.ts, kept equal by teams.mirror.test.ts).
 * Self-contained: no imports, so the gateway's mirror test can load this file directly.
 */

export interface SiteTeam {
  /** `team-…`, made once and never changed. */
  id: string;
  name: string;
  /** The team page's address: /teams/{slug}. Unique among the site's teams. */
  slug: string;
  /** The members' roster keys (an MLS agent's key, or a hand-added agent's id). Pages list them by the roster's order (resolveTeams). */
  member_keys: string[];
  /** The team lead's roster key, listed first. */
  leader_key?: string | null;
  bio?: string | null;
  photo_url?: string | null;
  phone?: string | null;
  email?: string | null;
  /** The office the team works from (an office's MLS id). Blank = the members' offices. */
  office_id?: string | null;
  /** The team has its own page at /teams/{slug}. Off = shown on the roster and office pages only. */
  has_page: boolean;
  /** Hidden from the live site (kept in the Agents tab to show again). */
  hidden?: boolean;
}

export const TEAM_NAME_MAX = 80;
export const TEAM_BIO_MAX = 2000;
/** A team is two or more agents. */
export const TEAM_MIN_MEMBERS = 2;
export const TEAM_MAX_MEMBERS = 50;

type AnyRecord = Record<string, unknown>;
const rec = (v: unknown): AnyRecord => (v && typeof v === 'object' && !Array.isArray(v) ? (v as AnyRecord) : {});
const str = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');
const optStr = (v: unknown, max = 500): string | null => {
  const s = str(v);
  return s ? s.slice(0, max) : null;
};

/** "The Smith Group!" → "the-smith-group". Never empty. */
export function teamSlugBase(name: string): string {
  const base = name
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)
    .replace(/-+$/g, '');
  return base || 'team';
}

/** A slug for `name` that no other team has: the-smith-group, then the-smith-group-2, … */
export function uniqueTeamSlug(name: string, taken: Iterable<string>): string {
  const used = new Set(taken);
  const base = teamSlugBase(name);
  if (!used.has(base)) return base;
  for (let n = 2; ; n++) {
    const candidate = `${base}-${n}`;
    if (!used.has(candidate)) return candidate;
  }
}

/** One stored team, cleaned; null when it has no id or name. */
export function normalizeTeam(entry: unknown): SiteTeam | null {
  const r = rec(entry);
  const id = str(r.id);
  const name = str(r.name).slice(0, TEAM_NAME_MAX);
  if (!id || !name) return null;
  const keys = Array.isArray(r.member_keys) ? r.member_keys.map(str).filter(Boolean) : [];
  const member_keys = Array.from(new Set(keys)).slice(0, TEAM_MAX_MEMBERS);
  const leader = str(r.leader_key);
  return {
    id,
    name,
    slug: str(r.slug) || teamSlugBase(name),
    member_keys,
    leader_key: leader && member_keys.includes(leader) ? leader : null,
    bio: optStr(r.bio, TEAM_BIO_MAX),
    photo_url: optStr(r.photo_url, 2000),
    phone: optStr(r.phone, 40),
    email: optStr(r.email, 200),
    office_id: optStr(r.office_id, 80),
    has_page: r.has_page !== false,
    ...(r.hidden === true ? { hidden: true } : {}),
  };
}

/** The site's teams as stored (`content.teams`), cleaned. A repeated id or slug keeps the first. */
export function normalizeTeams(raw: unknown): SiteTeam[] {
  const list = Array.isArray(raw) ? raw : [];
  const ids = new Set<string>();
  const slugs = new Set<string>();
  const out: SiteTeam[] = [];
  for (const entry of list) {
    const team = normalizeTeam(entry);
    if (!team || ids.has(team.id)) continue;
    if (slugs.has(team.slug)) team.slug = uniqueTeamSlug(team.name, slugs);
    ids.add(team.id);
    slugs.add(team.slug);
    out.push(team);
  }
  return out;
}

/** The roster fields a team reads: its key, and its offices for the office page. */
export interface TeamRosterAgent {
  key: string;
  officeMlsId?: string | null;
  offices?: string[] | null;
}

export interface ResolvedTeam<A extends TeamRosterAgent = TeamRosterAgent> {
  team: SiteTeam;
  /** The members the roster has, lead first, then in the roster's order (the Agents tab's Display
   *  order, then last names: agentOrder.ts), so a team lists its people the way every list does. */
  members: A[];
  /** /teams/{slug}, or null when the team has no page. */
  href: string | null;
}

export const teamHref = (team: Pick<SiteTeam, 'slug' | 'has_page'>): string | null =>
  team.has_page ? `/teams/${encodeURIComponent(team.slug)}` : null;

/**
 * The teams a visitor sees: not hidden, with at least one member still on the roster. Members are
 * looked up by roster key, so a hidden or departed agent drops out instead of showing as a blank.
 */
export function resolveTeams<A extends TeamRosterAgent>(rawTeams: unknown, roster: readonly A[]): Array<ResolvedTeam<A>> {
  const byKey = new Map<string, A>();
  const place = new Map<string, number>();
  roster.forEach((a, i) => {
    if (a.key && !byKey.has(a.key)) {
      byKey.set(a.key, a);
      place.set(a.key, i);
    }
  });
  const out: Array<ResolvedTeam<A>> = [];
  for (const team of normalizeTeams(rawTeams)) {
    if (team.hidden) continue;
    const rest = team.member_keys
      .filter((k) => k !== team.leader_key)
      .sort((a, b) => (place.get(a) ?? Infinity) - (place.get(b) ?? Infinity));
    const order = team.leader_key ? [team.leader_key, ...rest] : rest;
    const members = order.map((k) => byKey.get(k)).filter((a): a is A => Boolean(a));
    if (!members.length) continue;
    out.push({ team, members, href: teamHref(team) });
  }
  return out;
}

/** Every office id an agent belongs to. */
const officesOf = (a: TeamRosterAgent): string[] =>
  a.offices && a.offices.length ? a.offices : a.officeMlsId ? [a.officeMlsId] : [];

/** The offices a team works from: the one the owner set, else every office its members are in. */
export function teamOfficeIds(t: ResolvedTeam): string[] {
  if (t.team.office_id) return [t.team.office_id];
  return Array.from(new Set(t.members.flatMap(officesOf)));
}

/** The teams that belong on one office's page. */
export function teamsInOffice<A extends TeamRosterAgent>(teams: Array<ResolvedTeam<A>>, officeId: string): Array<ResolvedTeam<A>> {
  return teams.filter((t) => teamOfficeIds(t).includes(officeId));
}

/** Put a team in the list: replace the one with its id, else add it at the end. */
export function upsertTeam(list: readonly SiteTeam[], team: SiteTeam): SiteTeam[] {
  return list.some((t) => t.id === team.id) ? list.map((t) => (t.id === team.id ? team : t)) : [...list, team];
}

/** Take a team out of the list. Its agents are untouched. */
export function removeTeam(list: readonly SiteTeam[], id: string): SiteTeam[] {
  return list.filter((t) => t.id !== id);
}
